"use client";

import {
  Flag,
  Heart,
  Images,
  Link2,
  Lock,
  Pin,
  Quote,
  Repeat2,
  Send,
  Share2,
  Trash2,
} from "lucide-react";
import { usePathname, useRouter } from "next/navigation";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import type { FeedOriginalPost, FeedPost } from "@/actions/post";
import { AudienceSelector } from "@/components/audience-selector/audience-selector";
import { useAuthPrompt } from "@/components/auth";
import { FeedActionRow } from "@/components/feed/feed-action-row";
import {
  FeedCardAvatar,
  FeedCardIdentity,
} from "@/components/feed/feed-card-header";
import {
  FeedMediaViewer,
  type FeedMediaViewerItem,
} from "@/components/feed/feed-media-viewer";
import { FeedVideo } from "@/components/feed/feed-video";
import { RegularPostDrawer } from "@/components/feed/regular-post-drawer";
import { ReportFeedItemDialog } from "@/components/feed/report-feed-item-dialog";
import { AssistantAvatar } from "@/components/mcp/assistant-avatar";
import { MediaMusic, MediaMusicCredit } from "@/components/music/media-music";
import { SongCard } from "@/components/music/song-card";
import { ProfileLink } from "@/components/profile/profile-link";
import { SendToChatDrawer } from "@/components/share/send-to-chat-drawer";
import { SharedStickerEmbed } from "@/components/stickers/shared-sticker-embed";
import { AppImage as Image } from "@/components/ui/app-image";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import { MediaSoundButton } from "@/components/ui/media-sound-button";
import type { PostVisibility } from "@/db/schema/post";
import { useStoryViewer } from "@/features/stories/components/story-viewer/story-viewer-provider";
import {
  useStoryShareDraftMutation,
  useTrackFeedShareMutation,
} from "@/hooks/use-feed-share";
import {
  useDeletePostMutation,
  useRepostPostMutation,
  useTogglePostLikeMutation,
  useVotePostPollMutation,
} from "@/hooks/use-posts";
import { clampFeedMediaAspectRatio } from "@/lib/aspect-ratios";
import {
  formatClockTime,
  formatDayMonthYear,
  formatRelativeTimeCompact,
} from "@/lib/dates/relative-time";
import { pickActiveStripCard } from "@/lib/feed/media-strip-active";
import { extractFirstFeedUrl } from "@/lib/feed/rich-content";
import { toRenderableMedia } from "@/lib/media";
import { uiHaptics } from "@/lib/ui-haptics";
import { cn } from "@/lib/utils";
import { CommentsSheet, type CommentsSheetViewer } from "./comments-sheet";
import { ExpandablePostText } from "./expandable-post-text";
import { FeedEntityQuoteEmbed } from "./feed-entity-quote-embed";
import { LikesSheet } from "./likes-sheet";
import { MediaTagDots } from "./media-tag-dots";
import { OriginalPostEmbed } from "./original-post-embed";
import { PostEventTag } from "./post-event-tag";
import { PostLinkPreview } from "./post-link-preview";
import { PostLocationTag } from "./post-location-tag";
import { RichPostText } from "./rich-post-text";

interface PostProps {
  post: FeedPost;
  onOpenComments?: (post: FeedPost) => void;
  onOpenLikes?: (post: FeedPost) => void;
  viewer?: CommentsSheetViewer;
  /** Notification deep link: open comments to this comment and highlight it. */
  deepLinkCommentId?: number | null;
  /**
   * `detail`: the post's own page (X's focused tweet) — full text at a larger
   * size, an absolute timestamp, no feed divider.
   */
  variant?: "feed" | "detail";
}

export function Post({
  post,
  onOpenComments,
  onOpenLikes,
  viewer,
  deepLinkCommentId,
  variant = "feed",
}: PostProps) {
  const isDetail = variant === "detail";
  const [isCommentsOpen, setCommentsOpen] = useState(deepLinkCommentId != null);
  useEffect(() => {
    if (deepLinkCommentId != null) setCommentsOpen(true);
  }, [deepLinkCommentId]);
  const [isLikesOpen, setLikesOpen] = useState(false);
  const [isActionsOpen, setActionsOpen] = useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isQuoteOpen, setQuoteOpen] = useState(false);
  const [isRepostMenuOpen, setRepostMenuOpen] = useState(false);
  const [isReportOpen, setReportOpen] = useState(false);
  const defaultQuoteVisibility = (post.originalPost?.visibility ??
    post.visibility) as PostVisibility;
  // Audience for a plain repost — the user can widen/narrow who sees the
  // repost in their feed, defaulting to the original's audience.
  const [repostVisibility, setRepostVisibility] = useState<PostVisibility>(
    defaultQuoteVisibility,
  );
  const [isSendInChatOpen, setSendInChatOpen] = useState(false);
  const [shareCount, setShareCount] = useState(post.shareCount);
  const [mediaViewerIndex, setMediaViewerIndex] = useState<number | null>(null);

  const router = useRouter();
  const pathname = usePathname();
  // Per-instance salt so the same post rendered twice never collides in the
  // video autoplay/audio registry.
  const mediaAudioSeed = useId();
  const { openAuthPrompt } = useAuthPrompt();
  const { openComposer } = useStoryViewer();

  const toggleLikeMutation = useTogglePostLikeMutation();
  const repostMutation = useRepostPostMutation();
  const votePollMutation = useVotePostPollMutation();
  const deletePostMutation = useDeletePostMutation();
  const trackShareMutation = useTrackFeedShareMutation();
  const storyShareDraftMutation = useStoryShareDraftMutation();
  const isOptimistic =
    post.id < 0 || Boolean((post as { isOptimistic?: boolean }).isOptimistic);
  const isPrivatePreview = Boolean(post.isPrivatePreview);
  // The media strip is a free-scrolling native scroller (X's, no snapping).
  // The "active" card — the one whose sound and playback the post owns — only
  // changes once another card is FULLY in view; a card merely peeking in
  // never takes over. Ties keep the incumbent.
  const [activeSlide, setActiveSlide] = useState(0);
  const avatarRef = useRef<HTMLDivElement>(null);
  const stripFrameRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (stripFrameRef.current != null)
        cancelAnimationFrame(stripFrameRef.current);
    },
    [],
  );
  const handleStripScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const strip = event.currentTarget;
    // X eases the avatar down as the strip slides under its column. Written
    // straight to the node: a React render per scroll event would stutter.
    const progress = Math.min(1, Math.max(0, strip.scrollLeft / 56));
    if (avatarRef.current) {
      avatarRef.current.style.transform = `scale(${1 - 0.22 * progress})`;
    }
    if (stripFrameRef.current != null) return;
    stripFrameRef.current = requestAnimationFrame(() => {
      stripFrameRef.current = null;
      const bounds = strip.getBoundingClientRect();
      const cards = Array.from(strip.children, (child) =>
        child.getBoundingClientRect(),
      );
      const viewport = {
        left: Math.max(bounds.left, 0),
        right: Math.min(bounds.right, window.innerWidth),
      };
      setActiveSlide((current) =>
        pickActiveStripCard(cards, viewport, current),
      );
    });
  };
  const isOwner = Boolean(viewer?.id && viewer.id === post.authorId);
  const isAuthenticated = Boolean(viewer?.id);
  const promptForAuth = () => {
    openAuthPrompt({
      title: "Sign in to continue",
      description:
        "Use your Hiive account to like, comment, and see reactions.",
    });
  };

  useEffect(() => {
    setShareCount(post.shareCount);
  }, [post.shareCount]);

  // A plain repost of a post this viewer can see renders the ORIGINAL as the
  // card body (X's "↻ Name reposted" + the post itself). The repost row keeps
  // the actions, so likes/comments still land where they always did.
  const inlineOriginal =
    post.originalPost &&
    !(
      "isUnavailable" in post.originalPost && post.originalPost.isUnavailable
    ) &&
    !post.originalPost.isRestricted
      ? post.originalPost
      : null;
  const isPlainRepost =
    post.kind === "repost" &&
    Boolean(inlineOriginal) &&
    !(
      post.content ||
      post.media?.length ||
      post.poll ||
      post.contextTag ||
      post.locationTag ||
      post.quoteTarget ||
      post.sticker
    );
  const body: FeedPost | FeedOriginalPost =
    isPlainRepost && inlineOriginal ? inlineOriginal : post;
  const bodyMediaTags = isPlainRepost ? undefined : post.mediaTags;

  const mediaItems = (() => {
    if (!body.media?.length) return [] as FeedPost["media"];
    return [...body.media].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  })();
  const renderableMediaItems = mediaItems
    .map((item) => toRenderableMedia(item))
    .filter((item): item is NonNullable<ReturnType<typeof toRenderableMedia>> =>
      Boolean(item),
    );
  const firstMedia = renderableMediaItems[0];
  const mediaFrameAspect = clampFeedMediaAspectRatio(
    firstMedia?.width && firstMedia.height
      ? firstMedia.width / firstMedia.height
      : undefined,
  );
  // Quote/repost cards flip to a Twitter-style layout: text leads, the
  // author's own media renders as a trimmed inline block, and the referenced
  // card sits last. Everything else keeps the Instagram media-hero layout.
  const isQuoteLayout = Boolean(
    (post.originalPost && !isPlainRepost) || body.quoteTarget,
  );
  const hasLeadingMedia = renderableMediaItems.length > 0 && !isQuoteLayout;
  const isQuoteRepost =
    post.kind === "repost" &&
    Boolean(
      post.content ||
        hasLeadingMedia ||
        post.poll ||
        post.contextTag ||
        post.locationTag ||
        post.quoteTarget ||
        post.sticker,
    );

  const createdAtDate = new Date(body.createdAt);
  const feedLinkPreview = extractFirstFeedUrl(body.content);
  const timeAgo = formatRelativeTimeCompact(createdAtDate);
  const isAuthorBlocked = Boolean(body.author.isBlocked);
  const authorHandle = body.author.username;
  const repostTarget = post.originalPost ?? post;
  const repostTargetId = post.originalPostId ?? post.id;
  const isRepostTargetUnavailable =
    "isUnavailable" in repostTarget && Boolean(repostTarget.isUnavailable);
  // Anything this viewer can see can be reposted — public, buddies-only,
  // close-friends or a Hiive post. The original is re-checked per viewer when
  // the repost renders, so nobody outside its audience ever sees its content.
  const canRepost =
    !repostTarget.author.isBlocked &&
    !isRepostTargetUnavailable &&
    !isOptimistic &&
    !isPrivatePreview;
  const mediaViewerItems: FeedMediaViewerItem[] = renderableMediaItems.map(
    (item) => ({
      key: `post-${post.id}-${item.id}`,
      kind: item.kind,
      src: item.url,
      hlsSrc: item.hls?.url,
      posterSrc: item.poster?.url ?? null,
      blurHash: item.blurHash,
      // Processed video only has its HLS playlist (`url` is the .m3u8).
      downloadSrc: item.kind === "video" && !item.hls ? item.url : null,
    }),
  );

  const handleToggleLike = () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }
    toggleLikeMutation.mutate(
      { postId: post.id, currentlyLiked: post.isLikedByViewer },
      {
        onError: () => {
          toast.error("Couldn't update like right now");
        },
      },
    );
  };

  // Instagram-style double-tap-to-like on media. A single tap opens the
  // viewer after a short delay; a second tap within the window likes (never
  // unlikes) and fires the heart burst overlay instead.
  const DOUBLE_TAP_WINDOW_MS = 300;
  const [showHeartBurst, setShowHeartBurst] = useState(false);
  const lastMediaTapRef = useRef(0);
  const singleTapTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartBurstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (singleTapTimerRef.current) clearTimeout(singleTapTimerRef.current);
      if (heartBurstTimerRef.current) clearTimeout(heartBurstTimerRef.current);
    },
    [],
  );

  const handleMediaTap = (index: number) => {
    if (isPrivatePreview) {
      return;
    }

    const now = Date.now();
    if (now - lastMediaTapRef.current < DOUBLE_TAP_WINDOW_MS) {
      lastMediaTapRef.current = 0;
      if (singleTapTimerRef.current) {
        clearTimeout(singleTapTimerRef.current);
        singleTapTimerRef.current = null;
      }
      if (!post.isLikedByViewer) {
        uiHaptics.like();
        handleToggleLike();
      }
      setShowHeartBurst(true);
      if (heartBurstTimerRef.current) {
        clearTimeout(heartBurstTimerRef.current);
      }
      heartBurstTimerRef.current = setTimeout(
        () => setShowHeartBurst(false),
        900,
      );
      return;
    }

    lastMediaTapRef.current = now;
    singleTapTimerRef.current = setTimeout(() => {
      singleTapTimerRef.current = null;
      setMediaViewerIndex(index);
    }, DOUBLE_TAP_WINDOW_MS + 20);
  };

  // Also read during render (the share drawer's native payload), so it must
  // not touch `window` on the server.
  const getPostShareUrl = () =>
    typeof window === "undefined"
      ? `/post/${post.id}`
      : new URL(`/post/${post.id}`, window.location.origin).toString();

  const recordShare = (
    target: "system" | "copy" | "chat" | "native" | "story",
  ) => {
    trackShareMutation.mutate(
      { entityType: "post", entityId: post.id, target },
      {
        onSuccess: (result) => {
          setShareCount(result.shareCount);
        },
      },
    );
  };

  const handleCopyLink = async () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }

    const url = getPostShareUrl();

    try {
      await navigator.clipboard.writeText(url);
      recordShare("copy");
      toast.success("Link copied");
      setActionsOpen(false);
    } catch (_error) {
      toast.error("Couldn't copy link");
    }
  };

  const handleSharePost = () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }

    setActionsOpen(false);
    setSendInChatOpen(true);
  };

  const handleOpenQuote = () => {
    if (!canRepost) {
      toast.error("This post can't be reposted");
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }
    setActionsOpen(false);
    setRepostMenuOpen(false);
    setQuoteOpen(true);
  };

  const handleOpenRepostMenu = () => {
    if (!canRepost) {
      toast.error("This post can't be reposted");
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }
    setActionsOpen(false);
    setRepostMenuOpen(true);
  };

  const handleToggleRepost = () => {
    repostMutation.mutate(
      {
        postId: repostTargetId,
        currentlyReposted: post.isRepostedByViewer,
        visibility: repostVisibility,
      },
      {
        onSuccess: (result) => {
          toast.success(result.reposted ? "Reposted" : "Repost removed");
          setRepostMenuOpen(false);
        },
        onError: (error) => {
          toast.error(
            error instanceof Error ? error.message : "Couldn't update repost",
          );
        },
      },
    );
  };

  const handleShareToStory = async () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }

    try {
      const draft = await storyShareDraftMutation.mutateAsync({
        entityType: "post",
        entityId: String(post.id),
      });
      setActionsOpen(false);
      openComposer({ sharedEntity: draft.sharedEntity });
      recordShare("story");
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Couldn't prepare this story",
      );
    }
  };

  const handleOpenDeleteConfirm = () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    setActionsOpen(false);
    setDeleteConfirmOpen(true);
  };

  const handleDeletePost = () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }

    deletePostMutation.mutate(
      { id: post.id },
      {
        onSuccess: () => {
          toast.success("Post deleted");
          setDeleteConfirmOpen(false);
          setActionsOpen(false);

          if (pathname.startsWith(`/post/${post.id}`)) {
            router.replace("/feed");
          }
        },
        onError: () => {
          toast.error("Couldn't delete post right now");
        },
      },
    );
  };

  const handleOpenComments = () => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    if (onOpenComments) {
      onOpenComments(post);
      return;
    }
    setCommentsOpen(true);
  };

  const handleOpenLikes = () => {
    if (post.likeCount === 0) return;
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }
    if (onOpenLikes) {
      onOpenLikes(post);
      return;
    }
    setLikesOpen(true);
  };

  const handleVotePoll = (optionId: number) => {
    if (isOptimistic || isPrivatePreview) {
      return;
    }
    if (!isAuthenticated) {
      promptForAuth();
      return;
    }

    uiHaptics.select();
    votePollMutation.mutate(
      { postId: body.id, optionId },
      {
        onError: () => {
          toast.error("Couldn't vote right now");
        },
      },
    );
  };

  // Music plays over photos only: a video slide (or a moving KLIPY clip) keeps
  // its own sound, so the song's track unmounts while one is showing.
  const postMusic = isPrivatePreview ? null : (body.music ?? null);
  const activeMedia = renderableMediaItems[activeSlide] ?? firstMedia;
  const activeSlideIsStill = activeMedia?.kind === "image";

  const identity = (
    <FeedCardIdentity
      displayName={
        body.author.assistantName
          ? `${body.author.assistantName} · ${body.author.assistantHandle ? `@${body.author.assistantHandle}` : "AI-assisted"} · bot`
          : body.author.displayName || body.author.username || ""
      }
      handle={authorHandle}
      createdAt={createdAtDate}
      timeAgo={timeAgo}
      isBlocked={isAuthorBlocked}
      musicLine={
        postMusic ? (
          <MediaMusicCredit
            audioId={`${mediaAudioSeed}-music`}
            music={postMusic}
            active={activeSlideIsStill}
          />
        ) : null
      }
      extraBadge={
        post.kind === "repost" && !isQuoteRepost && !isPlainRepost ? (
          <span className="flex items-center gap-1 font-medium text-brand">
            <Repeat2 className="h-3 w-3" /> Reposted
          </span>
        ) : post.isPinned ? (
          <span className="flex items-center gap-1 font-medium text-brand">
            <Pin className="h-3 w-3" /> Pinned
          </span>
        ) : undefined
      }
    />
  );
  const avatar = body.author.assistantName ? (
    <AssistantAvatar
      ownerName={body.author.displayName}
      ownerAvatarUrl={body.author.avatarUrl}
    />
  ) : (
    <FeedCardAvatar
      displayName={body.author.displayName || body.author.username || ""}
      handle={authorHandle}
      avatarUrl={body.author.avatarUrl}
      isBlocked={isAuthorBlocked}
    />
  );
  const repostLine = isPlainRepost ? (
    <div className="-mb-2 flex items-center gap-2.5 px-3 pt-2.5 text-[13px] font-medium text-text-secondary">
      <span className="flex w-10 shrink-0 justify-end">
        <Repeat2 className="h-3.5 w-3.5" />
      </span>
      <ProfileLink
        handle={post.author.username}
        className="min-w-0 truncate hover:underline"
      >
        {viewer?.id && viewer.id === post.authorId
          ? "You reposted"
          : `${post.author.displayName || post.author.username} reposted`}
      </ProfileLink>
    </div>
  ) : null;
  const caption =
    !isPrivatePreview && body.content ? (
      isDetail ? (
        <div className="pt-3">
          <RichPostText
            content={body.content}
            mentions={body.mentions}
            className="text-[17px] leading-6"
          />
        </div>
      ) : (
        <div className="pt-0.5">
          {/* X density: a real paragraph before "Show more". Media posts
              stop a little earlier so the frame stays in the first glance. */}
          <ExpandablePostText
            content={body.content}
            mentions={body.mentions}
            clampLines={renderableMediaItems.length > 0 ? 5 : 10}
            textClassName="text-[15px] leading-5"
          />
        </div>
      )
    ) : null;

  const isMediaStrip = renderableMediaItems.length > 1;

  const renderMediaFill = (
    item: (typeof renderableMediaItems)[number],
    index: number,
  ): React.ReactNode =>
    item.provider === "klipy" ? (
      item.kind === "video" ? (
        <video
          src={item.url}
          className="h-full w-full object-cover"
          playsInline
          muted
          loop
          autoPlay
        />
      ) : (
        // biome-ignore lint/performance/noImgElement: animated KLIPY media must remain animated.
        <img
          src={item.url}
          alt="Post GIF or sticker"
          draggable={false}
          className="h-full w-full object-cover"
        />
      )
    ) : item.kind === "video" ? (
      <FeedVideo
        audioId={`${mediaAudioSeed}-${item.id}`}
        src={item.url}
        mimeType={item.mimeType || "video/mp4"}
        posterUrl={item.poster?.url}
        blurHash={item.blurHash}
        className={cn(
          "h-full w-full object-cover",
          isPrivatePreview && "scale-105 blur-md",
        )}
        forceMuted={isPrivatePreview}
        // In a strip only the current card may play — see activeSlide.
        playbackEnabled={!isMediaStrip || index === activeSlide}
      />
    ) : (
      <Image
        src={item.url}
        alt="Post media"
        fill
        className={cn("object-cover", isPrivatePreview && "scale-105 blur-md")}
        sizes={isMediaStrip ? "320px" : "(max-width: 768px) 100vw, 600px"}
      />
    );

  const renderMediaTile = (
    item: (typeof renderableMediaItems)[number],
    index: number,
  ) => (
    <>
      <button
        type="button"
        className={cn(
          "absolute inset-0 block overflow-hidden bg-surface-alt text-left",
          isPrivatePreview && "after:absolute after:inset-0 after:bg-black/30",
        )}
        onClick={() => handleMediaTap(index)}
        aria-label="Open media"
      >
        {renderMediaFill(item, index)}
        {isPrivatePreview && (
          <span className="absolute inset-0 z-10 flex items-center justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-glass-border-20 bg-black/55 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
              <Lock className="h-4 w-4" />
              Private post
            </span>
          </span>
        )}
      </button>
      {/* Instagram's corners: people bottom-left, sound bottom-right. */}
      {!isPrivatePreview &&
      item.kind === "video" &&
      item.provider !== "klipy" ? (
        <MediaSoundButton audioId={`${mediaAudioSeed}-${item.id}`} />
      ) : null}
      {/* In a strip, every photo card carries the song's speaker in the same
          corner a video card carries its own — so the control is always on
          the card you are looking at. */}
      {!isPrivatePreview &&
      postMusic &&
      isMediaStrip &&
      item.kind === "image" ? (
        <MediaSoundButton audioId={`${mediaAudioSeed}-music`} label="song" />
      ) : null}
      {!isPrivatePreview && bodyMediaTags?.[item.id]?.length ? (
        <MediaTagDots tags={bodyMediaTags[item.id]} />
      ) : null}
    </>
  );

  // X's media: one item sits in the content column as a rounded frame at its
  // stored (clamped) shape; several become a sideways strip of rounded cards
  // at one height, each at its own shape, bleeding to the screen edge so the
  // next one peeks in. No grid, no paging dots.
  const mediaBlock =
    renderableMediaItems.length > 0 ? (
      <div
        className={cn(
          "relative",
          isMediaStrip && (isDetail ? "-mx-3" : "-mr-3 -ml-[62px]"),
        )}
      >
        {isMediaStrip ? (
          <div
            onScroll={handleStripScroll}
            className={cn(
              "no-scrollbar flex gap-2 overflow-x-auto overscroll-x-contain",
              isDetail ? "px-3" : "pr-3 pl-[62px]",
            )}
          >
            {renderableMediaItems.map((item, index) => {
              const itemAspect =
                item.width && item.height ? item.width / item.height : 0.8;
              return (
                <div
                  key={`${body.id}-${item.id}`}
                  className="relative h-[300px] max-w-[78vw] shrink-0 overflow-hidden rounded-2xl border border-border sm:h-[360px] sm:max-w-[480px]"
                  style={{
                    aspectRatio: `${Math.min(1.5, Math.max(0.6, itemAspect))} / 1`,
                  }}
                >
                  {renderMediaTile(item, index)}
                </div>
              );
            })}
          </div>
        ) : firstMedia ? (
          <div
            className="relative w-full overflow-hidden rounded-2xl border border-border"
            style={{ aspectRatio: `${mediaFrameAspect} / 1` }}
          >
            {renderMediaTile(firstMedia, 0)}
          </div>
        ) : null}
        {/* Mounted for the post's whole life (see MediaMusic): `active` only
            moves it in and out of the playing registry. */}
        {postMusic ? (
          <MediaMusic
            audioId={`${mediaAudioSeed}-music`}
            music={postMusic}
            active={activeSlideIsStill}
            splitControls
            className={isMediaStrip ? "hidden" : undefined}
          />
        ) : null}
        {showHeartBurst ? (
          <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
            <Heart className="animate-heart-burst h-20 w-20 fill-white text-white drop-shadow-lg" />
          </div>
        ) : null}
        {mediaViewerIndex !== null && mediaViewerItems.length > 0 ? (
          <FeedMediaViewer
            items={mediaViewerItems}
            initialIndex={mediaViewerIndex}
            onClose={() => setMediaViewerIndex(null)}
            shareUrl={getPostShareUrl()}
            fileBaseName={`hiive-post-${post.id}`}
            onShared={recordShare}
            post={{
              author: {
                displayName:
                  body.author.displayName || body.author.username || "User",
                handle: isAuthorBlocked ? null : authorHandle,
                avatarUrl: isAuthorBlocked ? null : body.author.avatarUrl,
              },
              caption: body.content,
              music: postMusic,
              likeCount: post.likeCount,
              commentCount: post.commentCount,
              repostCount: post.repostCount,
              isLiked: post.isLikedByViewer,
              isReposted: post.isRepostedByViewer,
              // The auth prompt and the repost menu sit under the viewer,
              // so those paths close it first.
              onLike: () => {
                if (!isAuthenticated) setMediaViewerIndex(null);
                handleToggleLike();
              },
              onComment: handleOpenComments,
              onRepost: canRepost
                ? () => {
                    setMediaViewerIndex(null);
                    handleOpenRepostMenu();
                  }
                : undefined,
              onShare: () => {
                if (!isAuthenticated) setMediaViewerIndex(null);
                handleSharePost();
              },
              likeDisabled: toggleLikeMutation.isPending,
              repostDisabled: repostMutation.isPending,
              shareDisabled: trackShareMutation.isPending,
            }}
          />
        ) : null}
      </div>
    ) : null;

  const mainContent = (
    <>
      {caption}

      {isPrivatePreview ? (
        <div className="flex flex-col gap-2 pt-2.5">
          {mediaBlock ?? (
            <div className="relative h-36 overflow-hidden rounded-2xl bg-surface-alt">
              <div className="absolute inset-0 scale-105 bg-linear-to-br from-surface-alt via-background to-surface blur-md" />
              <div className="absolute inset-0 bg-black/30" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="inline-flex items-center gap-2 rounded-full border border-glass-border-20 bg-black/55 px-4 py-2 text-sm font-medium text-white backdrop-blur-sm">
                  <Lock className="h-4 w-4" />
                  Private post
                </span>
              </div>
            </div>
          )}
          <p className="pb-3 text-sm text-text-secondary">
            This post is private. Only buddies can view full content.
          </p>
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-2.5 pt-2.5 empty:hidden">
            {mediaBlock}

            {body.poll && (
              <PostPoll
                poll={body.poll}
                disabled={isOptimistic || votePollMutation.isPending}
                onVote={handleVotePoll}
              />
            )}

            {body.sticker ? (
              <SharedStickerEmbed
                stickerId={body.sticker.stickerId}
                owner={{ id: body.author.id, name: body.author.displayName }}
                viewerId={viewer?.id ?? null}
                width={176}
              />
            ) : null}

            {feedLinkPreview ? (
              <PostLinkPreview link={feedLinkPreview} compact />
            ) : null}

            {postMusic && renderableMediaItems.length === 0 ? (
              <SongCard music={postMusic} surface="post" />
            ) : null}

            {body.contextTag || body.locationTag ? (
              <div className="flex flex-wrap gap-1.5">
                {body.contextTag ? (
                  <PostEventTag tag={body.contextTag} compact asLink />
                ) : null}
                {body.locationTag ? (
                  <PostLocationTag location={body.locationTag} compact />
                ) : null}
              </div>
            ) : null}

            {post.originalPost && !isPlainRepost ? (
              <OriginalPostEmbed
                originalPost={post.originalPost}
                layout={renderableMediaItems.length > 0 ? "row" : "card"}
              />
            ) : null}

            {body.quoteTarget ? (
              <FeedEntityQuoteEmbed target={body.quoteTarget} />
            ) : null}
          </div>

          {isDetail ? (
            <p className="border-b border-border pt-3 pb-3 text-[14px] text-text-secondary">
              <time dateTime={createdAtDate.toISOString()}>
                {formatClockTime(createdAtDate)} ·{" "}
                {formatDayMonthYear(createdAtDate)}
              </time>
            </p>
          ) : null}

          <div className={cn(isDetail && "border-b border-border pt-1")}>
            <FeedActionRow
              layout="x"
              likeCount={post.likeCount}
              commentCount={post.commentCount}
              quoteCount={post.repostCount}
              shareCount={shareCount}
              isLiked={post.isLikedByViewer}
              isQuoteActive={post.isRepostedByViewer}
              onLike={handleToggleLike}
              onOpenLikes={handleOpenLikes}
              onComment={handleOpenComments}
              onQuote={handleOpenRepostMenu}
              onShare={handleSharePost}
              onMore={() => setActionsOpen(true)}
              disabled={isOptimistic}
              likeDisabled={toggleLikeMutation.isPending}
              quoteDisabled={!canRepost || repostMutation.isPending}
              quoteLabel="Repost"
              shareDisabled={trackShareMutation.isPending}
            />
          </div>
        </>
      )}
    </>
  );

  return (
    <article
      className={cn("bg-background", !isDetail && "border-b border-border")}
    >
      {repostLine}
      {isDetail ? (
        // X's focused post: identity across the top, then everything at full
        // width beneath it.
        <div className="px-3 pt-3">
          <div className="flex items-center gap-2.5">
            {avatar}
            {identity}
          </div>
          {mainContent}
        </div>
      ) : (
        // X's timeline: an avatar column, and one content column that the
        // text, media and actions all share.
        <div className="flex gap-2.5 px-3 pt-3">
          <div
            ref={avatarRef}
            className="h-fit origin-top-left transition-transform duration-200 ease-out will-change-transform"
          >
            {avatar}
          </div>
          <div className="min-w-0 flex-1">
            {identity}
            {mainContent}
          </div>
        </div>
      )}

      {isPrivatePreview ? null : (
        <>
          <CommentsSheet
            post={post}
            open={isCommentsOpen}
            onOpenChange={setCommentsOpen}
            viewer={viewer}
            targetCommentId={deepLinkCommentId}
          />
          <LikesSheet
            postId={post.id}
            open={isLikesOpen}
            onOpenChange={setLikesOpen}
            currentUserId={viewer?.id}
          />

          <Drawer open={isActionsOpen} onOpenChange={setActionsOpen} modal>
            <DrawerContent className="border-t border-border outline-none">
              <DrawerTitle className="sr-only">Post actions</DrawerTitle>
              <div className="mx-auto w-full max-w-lg p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
                <div className="flex flex-col gap-2">
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                    onClick={handleSharePost}
                    disabled={isOptimistic}
                  >
                    <Share2 className="h-5 w-5" />
                    Share
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                    onClick={() => {
                      setActionsOpen(false);
                      setSendInChatOpen(true);
                    }}
                    disabled={isOptimistic}
                  >
                    <Send className="h-5 w-5" />
                    Send in chat
                  </Button>

                  {/* Only public profile posts can be reshared to a story.
                      The author's per-user "allow resharing" toggle is
                      enforced server-side (getStoryShareDraft). */}
                  {post.type === "user" && post.visibility === "public" ? (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                      onClick={() => void handleShareToStory()}
                      disabled={
                        isOptimistic || storyShareDraftMutation.isPending
                      }
                    >
                      <Images className="h-5 w-5" />
                      Share to story
                    </Button>
                  ) : null}

                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                    onClick={() => void handleCopyLink()}
                    disabled={isOptimistic}
                  >
                    <Link2 className="h-5 w-5" />
                    Copy link
                  </Button>

                  <Button
                    type="button"
                    variant="ghost"
                    className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                    onClick={handleOpenQuote}
                    disabled={!canRepost || repostMutation.isPending}
                  >
                    <Quote className="h-5 w-5" />
                    Quote repost
                  </Button>

                  {!isOwner && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                      onClick={() => {
                        setActionsOpen(false);
                        setReportOpen(true);
                      }}
                    >
                      <Flag className="h-5 w-5" />
                      Report
                    </Button>
                  )}

                  {isOwner && (
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-12 w-full justify-start rounded-xl px-4 text-destructive"
                      onClick={handleOpenDeleteConfirm}
                      disabled={deletePostMutation.isPending || isOptimistic}
                    >
                      <Trash2 className="h-5 w-5" />
                      Delete post
                    </Button>
                  )}
                </div>

                <Button
                  type="button"
                  variant="outline"
                  className="mt-3 h-12 w-full rounded-xl"
                  onClick={() => setActionsOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </DrawerContent>
          </Drawer>

          <Drawer
            open={isRepostMenuOpen}
            onOpenChange={setRepostMenuOpen}
            modal
          >
            <DrawerContent className="border-t border-border outline-none">
              <DrawerTitle className="sr-only">Repost options</DrawerTitle>
              <div className="mx-auto w-full max-w-lg p-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
                {!post.isRepostedByViewer ? (
                  <div className="mb-2 flex items-center justify-between gap-3 rounded-xl px-4 py-2">
                    <span className="text-sm text-text-secondary">
                      Share your repost with
                    </span>
                    <AudienceSelector
                      value={repostVisibility}
                      onChange={(next) =>
                        setRepostVisibility(next as PostVisibility)
                      }
                      options={[
                        {
                          value: "private",
                          label: "Buddies only",
                          description: "Only your buddies see this repost.",
                        },
                        { value: "close_friends" },
                        { value: "public" },
                      ]}
                      disabled={repostMutation.isPending}
                    />
                  </div>
                ) : null}
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleToggleRepost}
                  disabled={repostMutation.isPending}
                >
                  <Repeat2 className="h-5 w-5" />
                  {post.isRepostedByViewer ? "Undo repost" : "Repost"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="h-12 w-full justify-start rounded-xl px-4 text-text-primary"
                  onClick={handleOpenQuote}
                  disabled={repostMutation.isPending}
                >
                  <Quote className="h-5 w-5" />
                  Quote
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="mt-3 h-12 w-full rounded-xl"
                  onClick={() => setRepostMenuOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </DrawerContent>
          </Drawer>
        </>
      )}

      {!isPrivatePreview && (
        <Dialog open={isDeleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete post?</DialogTitle>
              <DialogDescription>
                This will remove the post from everyone's feed. This action
                can't be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deletePostMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                onClick={handleDeletePost}
                disabled={deletePostMutation.isPending}
              >
                {deletePostMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {!isPrivatePreview && isQuoteOpen ? (
        <RegularPostDrawer
          open={isQuoteOpen}
          onOpenChange={setQuoteOpen}
          currentUser={{
            id: viewer?.id ?? "",
            displayName: viewer?.displayName ?? "You",
            avatarUrl: viewer?.avatarUrl ?? null,
          }}
          quoteTarget={{
            kind: "post",
            postId: repostTargetId,
            post: repostTarget,
            currentlyReposted: post.isRepostedByViewer,
            defaultVisibility: defaultQuoteVisibility,
          }}
          onSuccess={() => setQuoteOpen(false)}
        />
      ) : null}

      {!isPrivatePreview && !isOwner && (
        <ReportFeedItemDialog
          open={isReportOpen}
          onOpenChange={setReportOpen}
          targetUserId={post.author.id}
          targetLabel="post"
          entityType="post"
          entityId={post.id}
        />
      )}

      <SendToChatDrawer
        open={isSendInChatOpen}
        onOpenChange={setSendInChatOpen}
        payload={{
          type: "entity",
          entityType: "post",
          entityId: String(post.id),
        }}
        nativeShare={{
          title: post.author.displayName
            ? `${post.author.displayName} on Hiive`
            : "Hiive post",
          text: post.content ?? "View this post on Hiive",
          url: getPostShareUrl(),
        }}
        onShared={recordShare}
      />
    </article>
  );
}

function PostPoll({
  poll,
  disabled,
  onVote,
}: {
  poll: NonNullable<FeedPost["poll"]>;
  disabled: boolean;
  onVote: (optionId: number) => void;
}) {
  const showResults = poll.totalVotes > 0 || poll.viewerOptionId != null;

  return (
    <div className="space-y-2 rounded-xl border border-border bg-surface-alt/40 p-2.5">
      <div className="flex items-baseline justify-between gap-2">
        <RichPostText
          content={poll.question}
          mentions={poll.questionMentions}
          className="text-[15px] leading-snug font-semibold"
        />
        <p className="shrink-0 text-xs text-text-secondary">
          {poll.totalVotes.toLocaleString()}{" "}
          {poll.totalVotes === 1 ? "vote" : "votes"}
        </p>
      </div>

      <div className="space-y-1.5">
        {poll.options.map((option) => {
          const percent =
            poll.totalVotes > 0
              ? Math.round((option.voteCount / poll.totalVotes) * 100)
              : 0;

          return (
            <button
              key={option.id}
              type="button"
              onClick={() => onVote(option.id)}
              disabled={disabled || option.isVotedByViewer}
              className={cn(
                "relative min-h-9 w-full overflow-hidden rounded-lg border border-border bg-background px-2.5 py-1.5 text-left transition",
                "hover:border-brand/60 disabled:cursor-default",
                option.isVotedByViewer && "border-brand text-brand",
              )}
            >
              {showResults ? (
                <span
                  className="absolute inset-y-0 left-0 bg-brand/12 transition-[width]"
                  style={{ width: `${percent}%` }}
                />
              ) : null}
              <span className="relative flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{option.label}</span>
                {showResults ? (
                  <span className="text-xs font-semibold">{percent}%</span>
                ) : null}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
